// Invite a borrower or realtor to a lead (ticket 211): create the signup link,
// email it to the address on the invite, copy it, or turn it off.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail, Copy, Ban } from 'lucide-react';
import { api } from './api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from './errors';
import { copyText } from './clipboard';
import { formatDateTime } from './kit/time';

const KIND = { borrower: 'Borrower', realtor: 'Realtor' };

const NOT_SENT = {
  provider_not_configured: 'Email is not set up for this workspace.',
  frontend_url_not_set: 'The app address is not configured, so the link cannot be built.',
  send_failed: 'The email service refused it.',
};
const whyNotSent = (reason) => (
  reason?.startsWith('recipient_blocked') ? 'That address is blocked from receiving email.' : NOT_SENT[reason] || 'It could not be sent.'
);

// A Borrower invite goes to the lead, so it starts with the lead's name and email.
// A Realtor is a different person: both start blank (ticket 216).
const startForm = (type, lead) => (type === 'borrower'
  ? { invite_type: type, email: lead?.email || '', name: lead?.name || '' }
  : { invite_type: type, email: '', name: '' });

export default function LeadInviteDialog({ leadId, lead, open, onOpenChange }) {
  const [form, setForm] = useState(() => startForm('borrower', lead));
  const [invites, setInvites] = useState([]);
  const [listError, setListError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/invites`);
      setInvites(data.invites || []);
      setListError(null);
    } catch (e) {
      setListError(errorMessage(e, 'Could not load the open invites.'));
    }
  }, [leadId]);

  useEffect(() => { if (open) loadInvites(); }, [open, loadInvites]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setForm(startForm('borrower', lead)); }, [open, lead?.name, lead?.email]);

  const create = async () => {
    if (!form.email.trim()) { toast({ title: 'Email required' }); return; }
    setCreating(true);
    try {
      await api.post('/invites', { lead_id: leadId, ...form, email: form.email.trim() });
      toast({ title: 'Invite created', description: 'Email it below, or copy the link.' });
      setForm(startForm(form.invite_type, lead));
      loadInvites();
    } catch (e) { toast({ title: 'Failed', description: errorMessage(e) }); }
    setCreating(false);
  };

  const emailInvite = async (inv) => {
    // A real email to an outside person: one stray click must not send it.
    if (!(await confirmDialog({
      title: `Email this invite to the ${KIND[inv.invite_type].toLowerCase()}?`,
      description: `This sends the signup link to ${inv.email}.`,
      confirmText: 'Send invite',
    }))) return;
    setBusyId(inv.invite_id);
    try {
      const { data: r } = await api.post(`/invites/${inv.invite_id}/send`);
      if (r.email_sent) toast({ title: 'Invite sent', description: `Link emailed to ${r.sent_to}` });
      else toast({ title: 'Invite not sent', description: `${whyNotSent(r.reason)} Use Copy link instead.` });
      loadInvites();
    } catch (e) { toast({ title: 'Invite not sent', description: errorMessage(e) }); }
    setBusyId(null);
  };

  const copyInvite = async (inv) => {
    if (await copyText(window.location.origin + inv.signup_link)) toast({ title: 'Copied' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  const turnOff = async (inv) => {
    if (!(await confirmDialog({
      title: 'Turn this invite off?',
      description: 'The link stops working immediately. To invite them again, make a new invite.',
      confirmText: 'Turn off',
      destructive: true,
    }))) return;
    setBusyId(inv.invite_id);
    try {
      await api.post(`/invites/${inv.invite_id}/revoke`);
      toast({ title: 'Invite turned off' });
    } catch (e) { toast({ title: 'Could not turn it off', description: errorMessage(e) }); }
    setBusyId(null);
    loadInvites();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">Invite Borrower / Realtor</DialogTitle>
          <DialogDescription>They set a password from the link and are linked to this lead.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Type</Label>
            <Select value={form.invite_type} onValueChange={(v) => setForm(startForm(v, lead))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="borrower">Borrower</SelectItem><SelectItem value="realtor">Realtor</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="invite-email" /></div>
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="invite-name" /></div>
          <button onClick={create} disabled={creating} data-testid="invite-create" className="btn-gold w-full justify-center">Create Invite Link</button>
        </div>

        <div className="mt-2 pt-3 border-t border-slate-100" data-testid="open-invites">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Open invites</div>
          {listError && <div className="text-sm text-red-700" role="alert">{listError}</div>}
          {!listError && invites.length === 0 && <div className="text-sm text-slate-400">No open invites.</div>}
          {invites.map((inv) => (
            <div key={inv.invite_id} className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0" data-testid={`invite-row-${inv.invite_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>{KIND[inv.invite_type]} · {inv.email}</div>
                <div className="text-xs text-slate-500">{inv.emailed_at ? `Emailed ${formatDateTime(inv.emailed_at)}` : 'Not emailed yet'}</div>
              </div>
              <button type="button" onClick={() => emailInvite(inv)} disabled={busyId === inv.invite_id} data-testid={`invite-email-${inv.invite_id}`} title="Email this invite" className="p-2 rounded-lg text-slate-500 hover:text-brand-navy hover:bg-slate-100 disabled:opacity-50">
                {busyId === inv.invite_id ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              </button>
              {inv.signup_link && (
                <button type="button" onClick={() => copyInvite(inv)} data-testid={`invite-copy-${inv.invite_id}`} title="Copy link" className="p-2 rounded-lg text-slate-500 hover:text-brand-navy hover:bg-slate-100"><Copy size={14} /></button>
              )}
              <button type="button" onClick={() => turnOff(inv)} disabled={busyId === inv.invite_id} data-testid={`invite-off-${inv.invite_id}`} title="Turn this invite off" className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"><Ban size={14} /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
